import type { DatePickerProps } from 'antd'
import dayjs from 'dayjs'
import localeData from 'dayjs/plugin/localeData'
import weekday from 'dayjs/plugin/weekday'
import { useEffect } from 'react'
import styled from 'styled-components'
import DatePicker from '../../dataEntry/DatePicker/DatePicker'
import { FormRadio } from '../../dataEntry/FormRadio'
import { FormRadioGroup } from '../../dataEntry/FormRadioGroup'

dayjs.extend(weekday)
dayjs.extend(localeData)

export type NextReviewEmptyType = 'next-review-empty-value'

export type NextReviewValue = dayjs.Dayjs | NextReviewEmptyType

export type NextReviewInputRadioValue = {
  value: NextReviewValue
  text: string
}

export type NextReviewInputProps = {
  value?: NextReviewValue
  onChange?: (newDate: NextReviewValue) => void
  radioValues: Array<NextReviewInputRadioValue>
  disabledDate?: DatePickerProps['disabledDate']
}

export const NEXT_REVIEW_DATE_EMPTY_VALUE: NextReviewEmptyType =
  'next-review-empty-value'

export const NextReviewInput = ({
  value,
  onChange = () => {},
  radioValues,
  disabledDate
}: NextReviewInputProps) => {
  const onDatePickerChange = (newValue: dayjs.Dayjs | null) => {
    if (newValue === null) {
      onChange(NEXT_REVIEW_DATE_EMPTY_VALUE)
    } else {
      onChange(newValue)
    }
  }

  useEffect(() => {
    checkValue(value)
  }, [value])

  const onRadioChange = (newValue: string): void => {
    if (typeof newValue !== 'string') {
      throw new Error(
        `NextReviewInput => onRadioChange => [newValue] should be a string. Actual value: ${newValue}`
      )
    }

    // return emptyValue if value is empty string
    if (newValue === '') {
      onChange(NEXT_REVIEW_DATE_EMPTY_VALUE)

      return
    }

    const dateValue = dayjs(newValue)

    if (dateValue.isValid()) {
      onChange(dateValue)
    } else {
      throw new Error(
        `NextReviewInput => onRadioChange => Date is not valid. dateValue: ${dateValue}, newValue: ${newValue}`
      )
    }
  }

  const datePickerFormattedValue =
    value === NEXT_REVIEW_DATE_EMPTY_VALUE ? null : value

  return (
    <Wrapper data-testid={NEXT_REVIEW_INPUT_TEST_ID}>
      <DatePickerStyled
        disabledDate={disabledDate}
        value={datePickerFormattedValue}
        onChange={(e) => onDatePickerChange(e)}
        suffixIcon={null}
      />
      <FormRadioGroupStyled
        direction="row"
        value={getFormRadioGroupValue(value)}
        onChange={(e) => onRadioChange(e.target.value)}
        data-cy="radio-container"
      >
        {radioValues.map(({ value, text }) => (
          <FormRadioStyled
            data-cy={`radio-${text.split(' ').slice(0, 1)}`}
            key={getFormRadioGroupValue(value)}
            value={getFormRadioGroupValue(value)}
            text={text}
          />
        ))}
      </FormRadioGroupStyled>
    </Wrapper>
  )
}

export const NEXT_REVIEW_INPUT_TEST_ID = 'next-review-input-test-id'

const checkValue = (value: unknown) => {
  if (
    typeof value !== 'object' &&
    value !== NEXT_REVIEW_DATE_EMPTY_VALUE &&
    value !== '' &&
    value !== undefined &&
    value !== null
  ) {
    throw new Error(
      `NextReviewInput => checkValue => [value] should be of type Dayjs or NextReviewEmptyType. Actual value: ${value}`
    )
  }
}

const getFormRadioGroupValue = (
  value: dayjs.Dayjs | NextReviewEmptyType | undefined
) => {
  if (value === null) {
    return null
  }

  if (value === NEXT_REVIEW_DATE_EMPTY_VALUE || value === undefined) {
    // set value to empty string if it is of NextReviewEmptyType type
    return ''
  }

  return value.startOf('day').format()
}

const Wrapper = styled.div`
  display: flex;
  align-items: center;

  gap: 32px;

  ${({ theme }) => theme.mediaQueries.mobileDown} {
    flex-direction: column;
  }
`

const DatePickerStyled = styled(DatePicker)`
  width: initial;
  padding: 8px 16px;
  flex-grow: 0;
`

const FormRadioGroupStyled = styled(FormRadioGroup)`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  justify-content: space-between;
`

const FormRadioStyled = styled(FormRadio)`
  width: fit-content;
`

export default NextReviewInput
